import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { calendarApi } from '../api';
import { useSocket } from '../../../shared/socket/useSocket';

const DEFAULT_TIME_AREAS = [
    { id: 1, name: 'Trabajo', color: '#3b82f6', active: true, blocks: [1, 2, 3, 4, 5].map((d) => ({ day: d, start: 9, end: 17 })) },
    { id: 2, name: 'Citas', color: '#10b981', active: false, blocks: [2, 4].map((d) => ({ day: d, start: 14, end: 16 })) },
    { id: 3, name: 'Ejercicio', color: '#f59e0b', active: false, blocks: [1, 3, 5].map((d) => ({ day: d, start: 7, end: 8 })) },
];

export function useTimeAreas(userId) {
    return useQuery({
        queryKey: ['calendar', 'time_areas', userId],
        queryFn: async () => {
            const data = await calendarApi.fetchTimeAreas(userId);
            if (!data.success || !Array.isArray(data.value)) return DEFAULT_TIME_AREAS;

            let savedAreas = data.value;
            // Migración de datos legado: una sola área "Trabajo" — se completa con las otras
            // dos por defecto y se persiste, mismo comportamiento que tenía el useEffect
            // original (fire-and-forget, no bloquea la lectura si falla). Excepción deliberada a
            // la regla de "todo catch muestra feedback" (02.5-calendar.md §4 "a.6", mismo criterio
            // que la excepción nombrada en `policies/code.md`/`02.4-databases.md`): el usuario no
            // inició esta escritura, la lectura ya tuvo éxito con los datos migrados en memoria
            // (`savedAreas`), y un toast acá solo confundiría sobre un guardado en segundo plano
            // que no pidió. Si falla, la migración simplemente se reintenta en la próxima carga.
            if (savedAreas.length === 1 && savedAreas[0].name === 'Trabajo') {
                savedAreas = [savedAreas[0], DEFAULT_TIME_AREAS[1], DEFAULT_TIME_AREAS[2]];
                calendarApi.saveTimeAreas(userId, savedAreas).catch((e) => console.error(e));
            }
            return savedAreas;
        },
        enabled: !!userId,
        // 02.5-calendar.md §4 "a.7" -- mantenida viva por la invalidación de useSaveTimeAreas
        // (y el overlay pendingTimeAreas de a.3 durante un resize) -- un refetch al reenfocar
        // competiría con esos, mismo riesgo de stomping que policies/code.md marca para tasks.
        refetchOnWindowFocus: false,
    });
}

export function useSaveTimeAreas(userId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (value) => calendarApi.saveTimeAreas(userId, value),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['calendar', 'time_areas', userId] }),
    });
}

export function useCalendarView(userId) {
    return useQuery({
        queryKey: ['calendar', 'calendar_view', userId],
        queryFn: async () => {
            const data = await calendarApi.fetchCalendarView(userId);
            return data.success && data.value ? data.value : 'week';
        },
        enabled: !!userId,
        refetchOnWindowFocus: false,
    });
}

export function useSaveCalendarView(userId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (value) => calendarApi.saveCalendarView(userId, value),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['calendar', 'calendar_view', userId] }),
    });
}

export function useCalendars(userId) {
    return useQuery({
        queryKey: ['calendar', 'calendars', userId],
        queryFn: async () => {
            const data = await calendarApi.fetchCalendars(userId);
            return data.success && Array.isArray(data.calendars) ? data.calendars : [];
        },
        enabled: !!userId,
        refetchOnWindowFocus: false,
    });
}

export function useEvents({ start, end, onlyMine, userId, userNick, q, calendarIds }) {
    return useQuery({
        // Incluir `q` y `calendarIds` en el queryKey para refrescar al buscar
        queryKey: ['calendar', 'events', start, end, onlyMine, userId, q, calendarIds],
        queryFn: async () => {
            const data = await calendarApi.fetchEvents({ start, end, onlyMine, userId, userNick, q, calendarIds });
            if (!data.success) return [];
            // Defensa en profundidad (integración de los avances de main): `data.success` en
            // true no garantiza que `events` venga como array — evita un TypeError si el
            // shape de la respuesta llega inesperado.
            return (data.events || []).map((e) => ({
                ...e,
                start: new Date(e.start_time),
                end: new Date(e.end_time),
            }));
        },
        // Si hay `q`, permitimos buscar aunque no haya `start` y `end`
        enabled: (!!start && !!end) || !!q,
        refetchOnWindowFocus: false,
    });
}

// Las 4 mutaciones de escritura + el socket `calendar-update` invalidan cualquier variante
// cacheada de `events` (distintos rangos de fecha/onlyMine ya vistos) — mismo efecto que el
// `refreshKey` que forzaba un re-fetch incondicional antes de esta migración.
function invalidateEvents(qc) {
    return qc.invalidateQueries({ queryKey: ['calendar', 'events'] });
}

function useInvalidateEvents() {
    const qc = useQueryClient();
    return () => invalidateEvents(qc);
}

// 02.5-calendar.md §3/§8 "a.1" — antes, `ComponentCalendar.jsx` traía su propio handler de
// `calendar-update` que reconciliaba a mano el mirror `events` (dedup por id en CREATE,
// find-and-replace en UPDATE, filter en DELETE), reconstruyendo `start`/`end` desde
// `start_time`/`end_time` él mismo. Invalidation-first acá, mismo patrón que
// `useTableSocketSync`/`useWhatsAppSocketSync`: el mirror sigue vivo (todavía no se retira, eso
// es `a.2`) porque `ComponentCalendar.jsx` ya tiene un efecto que copia `eventsQuery.data` al
// mirror -- invalidar `eventsQuery` alcanza igual, solo que ahora cuesta un refetch en vez de un
// patch sincrónico. **Fan-out real** (§3, hallazgo de `/refine-plans`): el backend
// (`calendarRouter.js`) hace `io.emit` sin scope de sala -- a diferencia de `row-changed`
// (`useTableSocketSync`, sala `table:<iddata>`), CUALQUIER cliente conectado en CUALQUIER space
// refresca `['calendar','events']` ante cualquier CRUD de evento en todo el sistema. Verificar en
// vivo el volumen de red antes de encarar `a.2` (retiro del mirror).
export function useCalendarSocketSync() {
    const qc = useQueryClient();
    useSocket(useMemo(() => ({
        'calendar-update': () => invalidateEvents(qc),
    }), [qc]));
}

export function useCreateEvent() {
    const invalidate = useInvalidateEvents();
    return useMutation({
        mutationFn: (payload) => calendarApi.createEvent(payload),
        onSuccess: invalidate,
    });
}

export function useUpdateEvent() {
    const invalidate = useInvalidateEvents();
    return useMutation({
        mutationFn: ({ id, payload }) => calendarApi.updateEvent(id, payload),
        onSuccess: invalidate,
    });
}

export function useDeleteEvent() {
    const invalidate = useInvalidateEvents();
    return useMutation({
        mutationFn: (id) => calendarApi.deleteEvent(id),
        onSuccess: invalidate,
    });
}

export function useUpdateEventOrTaskTime() {
    const invalidate = useInvalidateEvents();
    return useMutation({
        mutationFn: ({ event, payload }) => calendarApi.updateEventOrTaskTime(event, payload),
        onSuccess: invalidate,
    });
}
